/**
 * 腾讯滑块验证码 Mixin
 * 提供可复用的腾讯验证码功能
 *
 * 使用方法：
 * 1. 在组件中引入: import tencentCaptchaMixin from '@/mixins/tencentCaptcha'
 * 2. 注册 mixin: mixins: [tencentCaptchaMixin]
 * 3. 调用方法: this.showTencentCaptcha(callback)
 *
 * 示例：
 * this.showTencentCaptcha((res) => {
 *   if (res.ret === 0) {
 *     // 验证成功，res.ticket 和 res.randstr 可用于后端校验
 *     console.log('验证成功', res.ticket, res.randstr);
 *   }
 * });
 */

import { captchaCheck, sendSmsCode } from '@/api/login';

export default {
  data() {
    return {
      // 腾讯验证码实例
      _captchaObj: null,
      // 验证码回调函数
      _captchaCallback: null
    };
  },

  mounted() {
    // 组件挂载时加载腾讯验证码脚本（但不立即初始化实例）
    this._loadTCaptchaScript();
  },

  beforeDestroy() {
    // 组件销毁时清理验证码实例
    if (this._captchaObj) {
      this._captchaObj.destroy();
      this._captchaObj = null;
    }
  },

  methods: {
    /**
     * 加载腾讯验证码脚本
     * @private
     */
    _loadTCaptchaScript() {
      if (window.TencentCaptcha) {
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://turing.captcha.qcloud.com/TCaptcha.js';
      script.async = true;
      script.onload = () => {
        // 脚本加载成功
      };
      script.onerror = () => {
        // 脚本加载失败
      };
      document.head.appendChild(script);
    },

    /**
     * 初始化腾讯验证码（延迟到使用时才初始化）
     * @private
     */
    _initTCaptcha() {
      try {
        if (window.TencentCaptcha && !this._captchaObj) {
          const self = this;
          this._captchaObj = new window.TencentCaptcha(
            this._captchaAppId,
            function(res) {
              self._tencentCaptchaCallback(res);
            }
          );
        }
      } catch (error) {
        this._loadErrorCallback();
      }
    },

    /**
     * 腾讯验证码回调处理
     * @private
     */
    _tencentCaptchaCallback(res) {
      if (this._captchaCallback) {
        this._captchaCallback(res);
      }
    },

    /**
     * 验证码加载错误处理（生成容灾票据）
     * @private
     */
    _loadErrorCallback() {
      const ticket = `trerror_1001_${this._captchaAppId}_${Math.floor(new Date().getTime() / 1000)}`;
      const fakeRes = {
        ret: 0,
        randstr: '@' + Math.random().toString(36).substr(2),
        ticket: ticket
      };

      if (this._captchaCallback) {
        this._captchaCallback(fakeRes);
      }

      this.$message.error('验证码组件加载失败，请刷新页面重试');
    },

    /**
     * 显示腾讯验证码（公共方法）
     * @param {Function} callback - 验证完成后的回调函数
     * @public
     */
    showTencentCaptcha(callback) {
      // 设置回调函数
      this._captchaCallback = callback;
      
      // 直接在这里定义 AppID，避免 data 合并问题
      const captchaAppId = '192177108';
      
      // 检查脚本是否加载
      if (!window.TencentCaptcha) {
        this.$message.error('验证码组件未加载，请刷新页面重试');
        return;
      }

      // 每次都创建新实例（不复用）
      try {
        const self = this;
        
        // 腾讯验证码 1.0 格式：AppId、回调函数、配置对象（3个参数）
        const captcha = new window.TencentCaptcha(
          captchaAppId,
          function(res) {
            if (self._captchaCallback) {
              self._captchaCallback(res);
            }
          },
          {
            needFeedBack: false,
            userLanguage: 'zh-cn'
          }
        );
        
        captcha.show();
      } catch (error) {
        this._loadErrorCallback();
      }
    },

    /**
     * 发送短信验证码（带腾讯滑块验证）
     * @param {String} phone - 手机号码
     * @param {Function} successCallback - 发送成功后的回调函数（可选）
     * @public
     */
    sendSmsCodeWithCaptcha(phone, successCallback) {
      // 验证手机号格式
      const reg = /^1[3-9]\d{9}$/;
      if (!reg.test(phone)) {
        this.$message.error('请输入正确的手机号码');
        return;
      }

      // 显示腾讯验证码
      this.showTencentCaptcha((res) => {
        if (res.ret === 0) {
          // 验证成功，发送短信验证码
          this._sendSmsCodeRequest(phone, res.ticket, res.randstr, successCallback);
        } else {
          // 验证失败或用户取消
          if (res.ret === 2) {
            this.$message.info('验证已取消');
          } else {
            this.$message.error('验证失败，请重试');
          }
        }
      });
    },

    /**
     * 发送短信验证码请求
     * @param {String} phone - 手机号码
     * @param {String} ticket - 验证票据
     * @param {String} randstr - 随机字符串
     * @param {Function} successCallback - 发送成功后的回调函数
     * @private
     */
    async _sendSmsCodeRequest(phone, ticket, randstr, successCallback) {
      if (!ticket || !randstr) {
        this.$message.error('验证信息不完整，请重新验证');
        return;
      }

      try {
        this.$message.success('正在验证...');

        // 首先校验验证码
        console.log('📡 调用接口: POST /captcha/check');
        const checkRes = await captchaCheck({
          ticket,
          randstr
        });

        if (checkRes.code !== 200) {
          this.$message.error('验证码校验失败，请重试');
          return;
        }

        // 验证码校验成功后，发送短信验证码
        this.$message.success('验证通过，正在发送短信验证码...');

        console.log('📡 调用接口: POST /resource/sms/code');
        const smsRes = await sendSmsCode({
          phonenumber: phone,
          ticket
        });

        if (smsRes.code === 200) {
          console.log('✅ 短信验证码发送成功，返回 uuid:', smsRes.data);
          this.$message.success('短信验证码已发送至您的手机');

          // 执行成功回调，传递 uuid
          if (successCallback && typeof successCallback === 'function') {
            successCallback(smsRes.data); // 传递 uuid
          }
        } else {
          this.$message.error(smsRes.msg || '短信验证码发送失败，请稍后重试');
        }
      } catch (error) {
        this.$message.error('短信验证码发送失败，请稍后重试');
      }
    }
  }
};
